import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiRefreshCw, FiAlertCircle, FiMapPin, FiClock, FiNavigation } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';

import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import Shimmer from '../components/Common/Shimmer';
import apiService from '../services/api';
import { TrainInfoData } from '../types';
import styles from './TrainInfoResultPage.module.css';

const TrainInfoResultPage: React.FC = () => {
  const { trainNumber } = useParams<{ trainNumber: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [trainData, setTrainData] = useState<TrainInfoData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (trainNumber) {
      fetchTrainInfo(trainNumber);
    } else {
      navigate('/train-info');
    }
  }, [trainNumber]);

  const fetchTrainInfo = async (trainNo: string) => {
    setIsLoading(true);
    setError(null);
    setTrainData(null);

    try {
      const response = await apiService.getTrainInfo(trainNo);

      if (response.success && response.data) {
        setTrainData(response.data);
        toast.success('Train information fetched successfully!');
      } else {
        setError(response.error || 'Failed to fetch train information');
        toast.error(response.error || 'Failed to fetch train information');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (trainNumber) {
      fetchTrainInfo(trainNumber);
    }
  };

  const handleNewSearch = () => {
    navigate('/train-info');
  };

  const formatRunningDays = (days: string) => {
    const daysMap = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days
      .split('')
      .map((day, index) => (day === '1' ? daysMap[index] : null))
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className={styles.trainInfoResultPage}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaTrain className={styles.titleIcon} />
              Train Information
            </h1>
            <div className={styles.headerActions}>
              <Button variant="outline" size="md" onClick={handleNewSearch}>
                <FiArrowLeft />
                New Search
              </Button>
              <Button variant="secondary" size="md" onClick={handleRefresh} isLoading={isLoading}>
                <FiRefreshCw />
                Refresh
              </Button>
            </div>
          </div>
          <p className={styles.subtitle}>Train {trainNumber}</p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <Shimmer type="card" count={2} />
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <FiAlertCircle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>Unable to Fetch Train Information</h3>
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={handleRefresh}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleNewSearch}>
                  New Search
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results */}
        {!isLoading && !error && trainData && (
          <div className={styles.resultsSection}>
            {/* Train Basic Info */}
            <Card className={styles.trainDetailsCard}>
              <div className={styles.trainHeader}>
                <div className={styles.trainMainInfo}>
                  <h2 className={styles.trainName}>
                    {trainData.trainInfo.train_no} - {trainData.trainInfo.train_name}
                  </h2>
                  <div className={styles.trainType}>{trainData.trainInfo.type}</div>
                </div>
              </div>

              <div className={styles.journeyInfo}>
                <div className={styles.journeyPoint}>
                  <FiMapPin className={styles.journeyIcon} />
                  <div className={styles.journeyDetails}>
                    <span className={styles.journeyLabel}>From</span>
                    <span className={styles.journeyValue}>
                      {trainData.trainInfo.from_stn_name} ({trainData.trainInfo.from_stn_code})
                    </span>
                    <span className={styles.journeyTime}>{trainData.trainInfo.from_time}</span>
                  </div>
                </div>

                <div className={styles.journeyArrow}>→</div>

                <div className={styles.journeyPoint}>
                  <FiNavigation className={styles.journeyIcon} />
                  <div className={styles.journeyDetails}>
                    <span className={styles.journeyLabel}>To</span>
                    <span className={styles.journeyValue}>
                      {trainData.trainInfo.to_stn_name} ({trainData.trainInfo.to_stn_code})
                    </span>
                    <span className={styles.journeyTime}>{trainData.trainInfo.to_time}</span>
                  </div>
                </div>
              </div>

              <div className={styles.trainMeta}>
                <div className={styles.metaItem}>
                  <FiClock className={styles.metaIcon} />
                  <span className={styles.metaLabel}>Duration:</span>
                  <span className={styles.metaValue}>{trainData.trainInfo.travel_time}</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Running Days:</span>
                  <span className={styles.metaValue}>
                    {formatRunningDays(trainData.trainInfo.running_days)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Route Details */}
            {trainData.route && trainData.route.length > 0 && (
              <Card className={styles.routeCard}>
                <h3 className={styles.routeTitle}>
                  <FiMapPin />
                  Complete Route ({trainData.route.length} Stations)
                </h3>

                <div className={styles.routeTable}>
                  <div className={styles.routeHeader}>
                    <div className={styles.colSNo}>S.No</div>
                    <div className={styles.colStation}>Station</div>
                    <div className={styles.colArrival}>Arrival</div>
                    <div className={styles.colDeparture}>Departure</div>
                    <div className={styles.colHalt}>Halt</div>
                    <div className={styles.colDistance}>Distance</div>
                    <div className={styles.colDay}>Day</div>
                  </div>

                  <div className={styles.routeBody}>
                    {trainData.route.map((station, index) => (
                      <div key={index} className={styles.routeRow}>
                        <div className={styles.colSNo}>{index + 1}</div>
                        <div className={styles.colStation}>
                          <div className={styles.stationName}>{station.stnName}</div>
                          <div className={styles.stationCode}>{station.stnCode}</div>
                        </div>
                        <div className={styles.colArrival}>{station.arrival || '--'}</div>
                        <div className={styles.colDeparture}>{station.departure || '--'}</div>
                        <div className={styles.colHalt}>{station.halt}</div>
                        <div className={styles.colDistance}>{station.distance} km</div>
                        <div className={styles.colDay}>Day {station.day}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainInfoResultPage;